import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import InventoryList from './InventoryList';
import ViewInventoryItem from './ViewInventoryItem';
import ResizablePanels from '../../components/Layout/ResizablePanels';
import './Inventory.css';

function Inventory() {
  const { t } = useTranslation('procurement');
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'view', 'edit', 'create'

  useEffect(() => {
    // Set toolbar based on current mode
    if (window.setToolbar) {
      if (viewMode === 'list') {
        window.setToolbar({
          title: t('materials.title'),
          buttons: [
            {
              label: t('materials.toolbar.newMaterial'),
              icon: '➕',
              onClick: handleNew,
              variant: 'primary'
            },
            {
              label: t('materials.toolbar.refresh'),
              icon: '🔄',
              onClick: handleRefresh
            }
          ]
        });
      } else if (viewMode === 'view') {
        window.setToolbar({
          title: t('materials.toolbar.viewMaterial'),
          showBack: true,
          onBack: handleBack,
          buttons: [
            {
              label: t('materials.toolbar.edit'),
              icon: '✏️',
              onClick: handleEdit,
              variant: 'secondary'
            },
            {
              label: t('materials.toolbar.delete'),
              icon: '🗑️',
              onClick: handleDelete,
              variant: 'danger'
            }
          ]
        });
      } else if (viewMode === 'edit') {
        window.setToolbar({
          title: t('materials.toolbar.editMaterial'),
          showBack: false,
          buttons: [
            {
              label: t('materials.toolbar.cancel'),
              onClick: handleCancel,
              variant: 'secondary'
            },
            {
              label: t('materials.toolbar.save'),
              icon: '💾',
              onClick: handleSave,
              variant: 'primary'
            }
          ]
        });
      } else if (viewMode === 'create') {
        window.setToolbar({
          title: t('materials.toolbar.newMaterial'),
          showBack: false,
          buttons: [
            {
              label: t('materials.toolbar.cancel'),
              onClick: handleCancel,
              variant: 'secondary'
            },
            {
              label: t('materials.toolbar.create'),
              icon: '➕',
              onClick: handleSave,
              variant: 'primary'
            }
          ]
        });
      }
    }

    return () => {
      if (window.setToolbar) {
        window.setToolbar(null);
      }
    };
  }, [viewMode, t]);

  const handleSelectMaterial = (materialId) => {
    setSelectedItemId(materialId);
    setViewMode('view');
  };

  const handleNew = () => {
    setSelectedItemId(null);
    setViewMode('create');
  };

  const handleEdit = () => {
    setViewMode('edit');
  };

  const handleDelete = () => {
    // Delete will be handled in ViewInventoryItem component
    if (window.viewMaterialRef && window.viewMaterialRef.handleDelete) {
      window.viewMaterialRef.handleDelete();
    }
  };

  const handleSave = () => {
    // Save will be handled in ViewInventoryItem component
    if (window.viewMaterialRef && window.viewMaterialRef.handleSave) {
      window.viewMaterialRef.handleSave();
    }
  };

  const handleCancel = () => {
    if (viewMode === 'create') {
      setViewMode('list');
      setSelectedItemId(null);
    } else {
      setViewMode('view');
    }
  };

  const handleBack = () => {
    setViewMode('list');
    setSelectedItemId(null);
  };

  const handleRefresh = () => {
    // Trigger refresh in InventoryList
    if (window.materialsListRef && window.materialsListRef.fetchMaterials) {
      window.materialsListRef.fetchMaterials();
    }
  };

  const handleMaterialSaved = (material) => {
    setSelectedItemId(material.item_id);
    setViewMode('view');
    // Refresh the list
    if (window.materialsListRef && window.materialsListRef.fetchMaterials) {
      window.materialsListRef.fetchMaterials();
    }
  };

  return (
    <div className="materials-page">
      <ResizablePanels
        leftPanel={
          <InventoryList
            ref={ref => window.materialsListRef = ref}
            onSelectItem={handleSelectMaterial}
            selectedItemId={selectedItemId}
            onNew={handleNew}
          />
        }
        rightPanel={
          viewMode !== 'list' ? (
            <ViewInventoryItem
              ref={ref => window.viewMaterialRef = ref}
              itemId={selectedItemId}
              onBack={handleBack}
              editMode={viewMode === 'edit'}
              createMode={viewMode === 'create'}
              onSave={handleMaterialSaved}
              onCancel={handleCancel}
              onEdit={handleEdit}
            />
          ) : null
        }
        leftCollapsed={viewMode !== 'list'}
        defaultLeftWidth={40}
        minLeftWidth={30}
        maxLeftWidth={70}
      />
    </div>
  );
}

export default Inventory;